import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Search, BookOpen, Package, ChevronRight, Loader2, ChefHat, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { recipesAPI } from '@/lib/api';
import { matchesSearch } from '@/lib/utils';

// ─── Recipe Detail (inline, mobile) ─────────────────────────────
function RecipeDetail({ recipe, onBack, onOpenSub }) {
    if (!recipe) return null;

    return (
        <div>
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 mb-3 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Retour
            </button>

            <h2 className="text-lg font-bold text-slate-900 mb-1">{recipe.name}</h2>
            {recipe.category && (
                <span className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full mb-3">{recipe.category}</span>
            )}

            {/* Photo + Rendement side by side */}
            <div className="flex gap-3 items-center mb-4 pl-[12.5%]">
                {recipe.image_url ? (
                    <div className="w-1/2 rounded-xl overflow-hidden border border-slate-200 flex-shrink-0" style={{ aspectRatio: '4/3' }}>
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${recipe.image_url}`}
                            alt={recipe.name}
                            className="w-full h-full object-cover"
                            data-testid="guide-recipe-image"
                        />
                    </div>
                ) : (
                    <div className="w-1/2 rounded-xl border-2 border-dashed border-slate-200 flex-shrink-0 flex items-center justify-center bg-slate-50" style={{ aspectRatio: '4/3' }}>
                        <ChefHat className="w-10 h-10 text-slate-300" />
                    </div>
                )}
                <div className="bg-slate-50 rounded-lg px-3 py-3 text-center flex-1">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Rendement</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{recipe.portions} {recipe.portions > 1 && (recipe.yield_unit === 'portion' || recipe.yield_unit === 'pièce') ? (recipe.yield_unit || 'portion') + 's' : (recipe.yield_unit || 'portion')}</p>
                </div>
            </div>

            {/* Allergènes */}
            {recipe.allergens && recipe.allergens.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-4 px-1" data-testid="guide-recipe-allergens">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                    <span className="text-xs font-medium text-orange-600 uppercase tracking-wider">Allergènes :</span>
                    {recipe.allergens.map((a) => (
                        <span key={a} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full border border-orange-200">
                            {a}
                        </span>
                    ))}
                </div>
            )}

            {/* Ingredients */}
            <h3 className="font-semibold text-slate-900 text-sm mb-2">Ingredients</h3>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
                {(recipe.ingredients || []).map((ing, i) => (
                    <div key={i} className="px-3 py-2.5 flex items-center gap-3">
                        {ing.type === 'recipe' ? (
                            ing.sub_recipe_image ? (
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-orange-200 bg-white">
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${ing.sub_recipe_image}`} alt="" className="w-full h-full object-contain" />
                                </div>
                            ) : (
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-50">
                                    <BookOpen className="w-5 h-5 text-orange-400" />
                                </div>
                            )
                        ) : (
                            ing.industrial_product_image ? (
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200 bg-white">
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${ing.industrial_product_image}`} alt="" className="w-full h-full object-contain" />
                                </div>
                            ) : (
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-50">
                                    <Package className="w-5 h-5 text-slate-400" />
                                </div>
                            )
                        )}
                        <div className="flex-1 min-w-0">
                            {ing.type === 'recipe' && ing.sub_recipe_id ? (
                                <button
                                    onClick={() => onOpenSub(ing.sub_recipe_id)}
                                    className="text-sm font-medium text-blue-600 active:text-blue-800 text-left"
                                >
                                    {ing.sub_recipe_name || 'Sous-recette'}
                                </button>
                            ) : (
                                <p className="text-sm font-medium text-slate-800 truncate">
                                    {ing.industrial_product_name || ing.sub_recipe_name || '-'}
                                </p>
                            )}
                            {ing.supplier_name && <p className="text-[11px] text-slate-400">{ing.supplier_name}</p>}
                            {ing.ingredient_allergens && ing.ingredient_allergens.length > 0 && (
                                <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                    {ing.ingredient_allergens.map((a) => (
                                        <span key={a} className="px-1 py-0 bg-orange-100 text-orange-600 text-[9px] font-medium rounded">
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-sm text-slate-700">{ing.quantity} {ing.quantity > 1 && (ing.unit === 'portion' || ing.unit === 'pièce') ? ing.unit + 's' : (ing.unit || '')}</p>
                        </div>
                    </div>
                ))}
                {(!recipe.ingredients || recipe.ingredients.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4">Aucun ingredient</p>
                )}
            </div>
        </div>
    );
}

// ─── Main Recipes Page ───────────────────────────────────────────
export default function GuideRecettesPage() {
    const navigate = useNavigate();
    const [allRecipes, setAllRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('recipes');
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeStack, setRecipeStack] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchRecipes = useCallback(async () => {
        try {
            const res = await recipesAPI.getAll({});
            // Guide du Sushiman : uniquement les recettes Fuji Express
            setAllRecipes(res.data.filter(r => r.fuji_express));
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

    // Filter by tab + search
    const filtered = useMemo(() => {
        let list = allRecipes;
        if (typeFilter === 'recipes') {
            list = list.filter(r => !r.is_sub_recipe);
        } else if (typeFilter === 'sub_recipes') {
            list = list.filter(r => r.is_sub_recipe);
        }
        if (search.trim()) {
            list = list.filter(r =>
                matchesSearch(r.name, search) ||
                matchesSearch(r.category, search)
            );
        }
        return list;
    }, [allRecipes, typeFilter, search]);

    // Group by category
    const grouped = useMemo(() => {
        const groups = {};
        filtered.forEach(r => {
            const cat = r.category || 'Sans categorie';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(r);
        });
        return Object.entries(groups).sort(([a], [b]) => {
            if (a === 'Sans categorie') return 1;
            if (b === 'Sans categorie') return -1;
            return a.localeCompare(b);
        });
    }, [filtered]);

    const openRecipe = async (id) => {
        setLoadingDetail(true);
        try {
            const res = await recipesAPI.getById(id);
            setSelectedRecipe(res.data);
            setRecipeStack([res.data]);
        } catch { /* ignore */ }
        finally { setLoadingDetail(false); }
    };

    const openSubRecipe = async (id) => {
        setLoadingDetail(true);
        try {
            const res = await recipesAPI.getById(id);
            setRecipeStack(prev => [...prev, res.data]);
            setSelectedRecipe(res.data);
        } catch { /* ignore */ }
        finally { setLoadingDetail(false); }
    };

    const goBack = () => {
        if (recipeStack.length > 1) {
            const newStack = recipeStack.slice(0, -1);
            setRecipeStack(newStack);
            setSelectedRecipe(newStack[newStack.length - 1]);
        } else {
            setSelectedRecipe(null);
            setRecipeStack([]);
        }
    };

    // Detail view
    if (selectedRecipe) {
        return (
            <div className="px-4 py-5" data-testid="guide-recette-detail">
                {loadingDetail ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : (
                    <>
                        {recipeStack.length > 1 && (
                            <div className="flex items-center gap-1 text-[11px] text-slate-400 mb-2 flex-wrap">
                                {recipeStack.map((r, i) => (
                                    <span key={i} className="flex items-center gap-1">
                                        {i > 0 && <span>/</span>}
                                        <button
                                            onClick={() => {
                                                if (i < recipeStack.length - 1) {
                                                    setRecipeStack(prev => prev.slice(0, i + 1));
                                                    setSelectedRecipe(recipeStack[i]);
                                                }
                                            }}
                                            className={i < recipeStack.length - 1 ? 'active:underline text-slate-500' : 'font-medium text-slate-700'}
                                        >
                                            {r.name}
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <RecipeDetail recipe={selectedRecipe} onBack={goBack} onOpenSub={openSubRecipe} />
                    </>
                )}
            </div>
        );
    }

    const tabs = [
        { value: 'recipes', label: 'Recettes' },
        { value: 'sub_recipes', label: 'Sous-recettes' },
        { value: 'all', label: 'Tout' },
    ];

    // List view
    return (
        <div className="px-4 py-5" data-testid="guide-recettes">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <h1 className="text-xl font-bold text-slate-900 mb-3">Recettes</h1>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4">
                {tabs.map(tab => (
                    <button
                        key={tab.value}
                        onClick={() => setTypeFilter(tab.value)}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            typeFilter === tab.value
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 active:text-slate-700'
                        }`}
                        data-testid={`guide-tab-${tab.value}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                    className="pl-9 h-11 text-sm rounded-xl"
                    placeholder="Rechercher..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="guide-recipe-search"
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12">
                    <ChefHat className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">Aucune recette trouvee</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {grouped.map(([category, catRecipes]) => (
                        <div key={category}>
                            <div className="flex items-center gap-2 mb-1.5 px-1">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{category}</p>
                                <span className="text-[10px] text-slate-300">({catRecipes.length})</span>
                            </div>
                            <div className="space-y-1.5">
                                {catRecipes.map(r => (
                                    <button
                                        key={r.id}
                                        onClick={() => openRecipe(r.id)}
                                        className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 active:bg-slate-50 transition-colors text-left"
                                        data-testid={`guide-recipe-${r.id}`}
                                    >
                                        {r.image_url ? (
                                            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                                <img src={`${process.env.REACT_APP_BACKEND_URL}${r.image_url}`} alt={r.name} className="w-full h-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${r.is_sub_recipe ? 'bg-violet-50' : 'bg-amber-50'}`}>
                                                {r.is_sub_recipe
                                                    ? <BookOpen className="w-5 h-5 text-violet-500" />
                                                    : <ChefHat className="w-5 h-5 text-amber-600" />}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className="font-medium text-sm text-slate-900 truncate">{r.name}</p>
                                                {r.is_sub_recipe && (
                                                    <span className="text-[9px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">sous-recette</span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-400">{r.portions} {r.portions > 1 && (r.yield_unit === 'portion' || r.yield_unit === 'pièce' || !r.yield_unit) ? ((r.yield_unit || 'portion') + 's') : (r.yield_unit || 'portion')}</p>
                                            {r.allergens && r.allergens.length > 0 && (
                                                <div className="flex items-center gap-0.5 flex-wrap mt-0.5">
                                                    {r.allergens.map((a) => (
                                                        <span key={a} className="px-1 py-0 bg-orange-100 text-orange-600 text-[9px] font-medium rounded">
                                                            {a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
